/**
 * Level16Scene - 关卡16：黑洞引力
 * @description 教学目标：理解黑洞引力场对物体的影响
 * 核心公式：
 * - 引力加速度: a = GM/r²
 * - 史瓦西半径: Rs = 2GM/c²
 * - 逃逸速度: v_escape = √(2GM/r)
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import type { LevelConfig } from '@/levels/LevelConfig.ts';
import { TutorialUI } from '@/ui/TutorialUI.ts';
import { getTutorial } from '@/data/tutorials.ts';
import { getGameState } from '@/core/GameState.ts';
import { Blackhole } from '@/physics/Blackhole.ts';

// 关卡16特定配置
interface Level16SpecificConfig {
  /** 黑洞质量（游戏单位） */
  blackholeMass: number;
  /** 黑洞位置 x */
  blackholeX: number;
  /** 黑洞位置 y */
  blackholeY: number;
  /** 安全区域位置 x */
  safeZoneX: number;
  /** 安全区域位置 y */
  safeZoneY: number;
  /** 安全区域半径 */
  safeZoneRadius: number;
  /** 可选初始速度列表 */
  initialVelocityOptions: number[];
  /** 逃逸速度参考值 */
  escapeVelocityRef: number;
  /** 公式 */
  formula: string;
  /** 提示 */
  hint: string;
}

// 游戏状态类型
type GameState = 'idle' | 'running' | 'success' | 'failed';

// 游戏尺寸常量
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

/**
 * Level16Scene 类
 * 关卡16场景，实现黑洞引力教学
 */
export class Level16Scene extends Phaser.Scene {
  // 关卡配置
  private levelConfig: LevelConfig | null = null;
  private levelSpecific: Level16SpecificConfig | null = null;

  // 游戏状态
  private gameState: GameState = 'idle';
  private elapsedTime: number = 0;

  // 黑洞物理模块
  private blackhole: Blackhole | null = null;

  // 火柴人属性
  private stickman: Phaser.GameObjects.Graphics | null = null;
  private stickmanPosition: { x: number; y: number } = { x: 500, y: 300 };
  private stickmanVelocity: { x: number; y: number } = { x: 0, y: 0 };
  private selectedInitialVelocity: number = 0;

  // 安全区域
  private safeZone: Phaser.GameObjects.Graphics | null = null;

  // 黑洞可视化
  private blackholeGraphics: Phaser.GameObjects.Graphics | null = null;

  // 引力场可视化
  private gravityFieldGraphics: Phaser.GameObjects.Graphics | null = null;

  // 轨迹记录
  private trajectoryPoints: Array<{ x: number; y: number }> = [];
  private trajectoryGraphics: Phaser.GameObjects.Graphics | null = null;

  // UI 元素
  private velocityText: Phaser.GameObjects.Text | null = null;
  private distanceText: Phaser.GameObjects.Text | null = null;
  private escapeVelText: Phaser.GameObjects.Text | null = null;
  private hintText: Phaser.GameObjects.Text | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;
  private velocityButtons: Phaser.GameObjects.Graphics[] = [];
  private startButton: Phaser.GameObjects.Graphics | null = null;

  // 教程组件
  private tutorialUI: TutorialUI | null = null;

  constructor() {
    super({ key: 'Level16Scene' });
  }

  preload(): void {
    // 加载关卡配置
    this.load.json('level-16', 'src/levels/levels/level-16.json');
  }

  create(): void {
    // 加载关卡配置
    this.levelConfig = this.cache.json.get('level-16') as LevelConfig;

    if (this.levelConfig && this.levelConfig.levelSpecific) {
      this.levelSpecific = this.levelConfig.levelSpecific as unknown as Level16SpecificConfig;
    }

    // 初始化黑洞
    if (this.levelSpecific) {
      this.blackhole = new Blackhole({
        id: 'main-blackhole',
        position: Vec2(this.levelSpecific.blackholeX, this.levelSpecific.blackholeY),
        mass: this.levelSpecific.blackholeMass,
      });
    }

    // 创建背景
    this.createBackground();

    // 创建黑洞可视化
    this.createBlackholeVisualization();

    // 创建引力场可视化
    this.createGravityFieldVisualization();

    // 创建安全区域
    this.createSafeZone();

    // 创建火柴人
    this.createStickman();

    // 创建轨迹画布
    this.trajectoryGraphics = this.add.graphics();
    this.trajectoryGraphics.setDepth(3);

    // 创建 UI
    this.createUI();

    // 设置输入事件
    this.setupInputEvents();

    // 显示教程
    this.showTutorial();

    // 初始化显示
    this.updatePhysicsDisplay();
  }

  override update(_time: number, delta: number): void {
    if (this.gameState !== 'running') return;

    // 更新时间
    this.elapsedTime += delta / 1000;

    // 更新火柴人运动（受引力影响）
    this.updateStickmanMovement(delta);

    // 更新可视化效果
    this.updateVisualEffects();

    // 检测胜利/失败
    this.checkGameEnd();
  }

  /**
   * 创建背景
   */
  private createBackground(): void {
    // 深空背景
    this.cameras.main.setBackgroundColor('#050510');

    // 添加星空效果
    const starfield = this.add.graphics();
    starfield.setDepth(0);

    // 绘制星星
    for (let i = 0; i < 100; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * GAME_HEIGHT;
      const size = Math.random() * 2 + 0.5;
      const brightness = Math.random() * 0.5 + 0.3;

      starfield.fillStyle(0xffffff, brightness);
      starfield.fillCircle(x, y, size);
    }

    // 添加网格线作为参考
    const gridGraphics = this.add.graphics();
    gridGraphics.lineStyle(1, 0x1a1a3a, 0.2);

    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(x, 0);
      gridGraphics.lineTo(x, GAME_HEIGHT);
      gridGraphics.strokePath();
    }

    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(0, y);
      gridGraphics.lineTo(GAME_WIDTH, y);
      gridGraphics.strokePath();
    }
  }

  /**
   * 创建黑洞可视化
   */
  private createBlackholeVisualization(): void {
    if (!this.blackhole) return;

    this.blackholeGraphics = this.add.graphics();
    this.blackholeGraphics.setDepth(5);

    const info = this.blackhole.getInfo();
    const screenX = info.position.x;
    const screenY = GAME_HEIGHT - info.position.y;

    // 绘制事件视界（黑色核心）
    this.blackholeGraphics.fillStyle(0x000000, 1);
    this.blackholeGraphics.fillCircle(screenX, screenY, info.schwarzschildRadius);

    // 绘制事件视界边界（红色警示）
    this.blackholeGraphics.lineStyle(3, 0xff0000, 0.8);
    this.blackholeGraphics.strokeCircle(screenX, screenY, info.schwarzschildRadius);

    // 绘制吸积盘效果（外圈）
    const accretionDiskRadius = info.schwarzschildRadius * 2;
    this.blackholeGraphics.lineStyle(8, 0xff6600, 0.6);
    this.blackholeGraphics.strokeCircle(screenX, screenY, accretionDiskRadius);

    // 绘制吸积盘发光效果
    this.blackholeGraphics.fillStyle(0xff3300, 0.2);
    this.blackholeGraphics.fillCircle(screenX, screenY, accretionDiskRadius * 1.5);

    // 添加黑洞标签
    this.add.text(screenX, screenY + info.schwarzschildRadius + 30, '黑洞中心', {
      fontSize: '12px',
      color: '#ff0000',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 显示史瓦西半径数值
    this.add.text(
      screenX + info.schwarzschildRadius + 20,
      screenY - info.schwarzschildRadius,
      `Rs = ${info.schwarzschildRadius.toFixed(1)}`,
      {
        fontSize: '11px',
        color: '#ff6600',
        fontFamily: 'Arial',
      }
    ).setOrigin(0);
  }

  /**
   * 创建引力场可视化
   */
  private createGravityFieldVisualization(): void {
    if (!this.blackhole) return;

    this.gravityFieldGraphics = this.add.graphics();
    this.gravityFieldGraphics.setDepth(1);

    const info = this.blackhole.getInfo();
    const screenX = info.position.x;
    const screenY = GAME_HEIGHT - info.position.y;

    // 绘制引力场强度同心圆
    const maxRadius = 250;
    const ringCount = 5;

    for (let i = 1; i <= ringCount; i++) {
      const radius = info.schwarzschildRadius * (i * 2);
      if (radius > maxRadius) break;

      // 引力场强度随距离减弱，颜色从红到蓝渐变
      const strength = 1 - i / ringCount;
      const red = Math.floor(255 * strength);
      const blue = Math.floor(255 * (1 - strength));
      const color = (red << 16) | (blue);

      this.gravityFieldGraphics.lineStyle(2, color, 0.3);
      this.gravityFieldGraphics.strokeCircle(screenX, screenY, radius);
    }

    // 绘制引力方向箭头（示意）
    const arrowGraphics = this.add.graphics();
    arrowGraphics.setDepth(2);

    // 在几个关键位置绘制指向黑洞的箭头
    const arrowPositions = [
      { x: screenX + 150, y: screenY },
      { x: screenX - 150, y: screenY },
      { x: screenX, y: screenY + 150 },
      { x: screenX, y: screenY - 150 },
    ];

    arrowPositions.forEach(pos => {
      // 绘制指向黑洞的小箭头
      const dx = screenX - pos.x;
      const dy = screenY - pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > info.schwarzschildRadius) {
        const unitX = dx / dist;
        const unitY = dy / dist;
        const arrowLength = 20;

        arrowGraphics.lineStyle(2, 0xff6600, 0.6);
        arrowGraphics.beginPath();
        arrowGraphics.moveTo(pos.x, pos.y);
        arrowGraphics.lineTo(pos.x + unitX * arrowLength, pos.y + unitY * arrowLength);
        arrowGraphics.strokePath();
      }
    });
  }

  /**
   * 创建安全区域
   */
  private createSafeZone(): void {
    if (!this.levelSpecific) return;

    this.safeZone = this.add.graphics();
    this.safeZone.setDepth(4);

    this.safeZone.fillStyle(0x4ade80, 0.3);
    this.safeZone.fillCircle(
      this.levelSpecific.safeZoneX,
      GAME_HEIGHT - this.levelSpecific.safeZoneY,
      this.levelSpecific.safeZoneRadius
    );
    this.safeZone.lineStyle(3, 0x4ade80);
    this.safeZone.strokeCircle(
      this.levelSpecific.safeZoneX,
      GAME_HEIGHT - this.levelSpecific.safeZoneY,
      this.levelSpecific.safeZoneRadius
    );

    this.add.text(
      this.levelSpecific.safeZoneX,
      GAME_HEIGHT - this.levelSpecific.safeZoneY + this.levelSpecific.safeZoneRadius + 20,
      '安全区域',
      {
        fontSize: '14px',
        color: '#4ade80',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);
  }

  /**
   * 创建火柴人
   */
  private createStickman(): void {
    const playerConfig = this.levelConfig?.player;
    this.stickmanPosition = {
      x: playerConfig?.startPosition.x ?? 500,
      y: playerConfig?.startPosition.y ?? 300,
    };

    this.stickman = this.add.graphics();
    this.stickman.setDepth(6);
    this.renderStickman();
  }

  /**
   * 渲染火柴人
   */
  private renderStickman(): void {
    if (!this.stickman) return;

    this.stickman.clear();

    const screenX = this.stickmanPosition.x;
    const screenY = GAME_HEIGHT - this.stickmanPosition.y;

    // 根据速度大小决定颜色
    const velocityMagnitude = Math.sqrt(
      this.stickmanVelocity.x * this.stickmanVelocity.x +
      this.stickmanVelocity.y * this.stickmanVelocity.y
    );

    // 颜色：低速蓝色，高速金色
    let color: number;
    if (velocityMagnitude < 10) {
      color = 0x00aaff; // 蓝色 - 低速
    } else if (velocityMagnitude < 30) {
      color = 0x00ff88; // 绿色 - 中速
    } else {
      color = 0xffaa00; // 金色 - 高速
    }

    // 火柴人头部（圆形）
    this.stickman.fillStyle(color, 1);
    this.stickman.fillCircle(screenX, screenY - 25, 15);

    // 火柴人躯干（线条）
    this.stickman.lineStyle(3, color, 1);
    this.stickman.beginPath();
    this.stickman.moveTo(screenX, screenY - 10);
    this.stickman.lineTo(screenX, screenY + 20);
    this.stickman.strokePath();

    // 火柴人手臂
    this.stickman.beginPath();
    this.stickman.moveTo(screenX - 15, screenY);
    this.stickman.lineTo(screenX + 15, screenY);
    this.stickman.strokePath();

    // 火柴人腿部
    this.stickman.beginPath();
    this.stickman.moveTo(screenX, screenY + 20);
    this.stickman.lineTo(screenX - 12, screenY + 40);
    this.stickman.moveTo(screenX, screenY + 20);
    this.stickman.lineTo(screenX + 12, screenY + 40);
    this.stickman.strokePath();

    // 如果在事件视界附近，添加警示效果
    if (this.blackhole && this.blackhole.isNearEventHorizon(Vec2(this.stickmanPosition.x, this.stickmanPosition.y), 2)) {
      this.stickman.fillStyle(0xff0000, 0.3);
      this.stickman.fillCircle(screenX, screenY, 30);
    }
  }

  /**
   * 创建 UI
   */
  private createUI(): void {
    // 标题
    this.add.text(GAME_WIDTH / 2, 40, '关卡16：黑洞引力', {
      fontSize: '28px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 公式显示
    this.add.text(GAME_WIDTH / 2, 75, '引力加速度: a = GM/r²', {
      fontSize: '18px',
      color: '#ffff00',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 史瓦西半径公式
    this.add.text(GAME_WIDTH / 2, 100, '史瓦西半径: Rs = 2GM/c²', {
      fontSize: '14px',
      color: '#ff6600',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 逃逸速度公式
    this.add.text(GAME_WIDTH / 2, 120, '逃逸速度: v_escape = √(2GM/r)', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 提示文本
    this.hintText = this.add.text(
      GAME_WIDTH / 2,
      145,
      this.levelSpecific?.hint ?? '选择初速度，逃离黑洞引力场',
      {
        fontSize: '16px',
        color: '#888888',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    // 物理信息显示面板
    this.createPhysicsPanel();

    // 速度选择按钮
    this.createVelocityButtons();

    // 开始按钮
    this.createStartButton();

    // 结果文本（初始隐藏）
    this.resultText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '', {
      fontSize: '28px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5).setVisible(false);
  }

  /**
   * 创建物理信息显示面板
   */
  private createPhysicsPanel(): void {
    const panelX = 100;
    const panelY = 180;
    const panelWidth = 180;
    const panelHeight = 100;

    const panel = this.add.graphics();
    panel.fillStyle(0x1a1a3a, 0.8);
    panel.fillRoundedRect(panelX, panelY, panelWidth, panelHeight, 10);
    panel.lineStyle(2, 0x3a3a5a);
    panel.strokeRoundedRect(panelX, panelY, panelWidth, panelHeight, 10);

    // 当前速度
    this.add.text(panelX + panelWidth / 2, panelY + 15, '当前速度', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.velocityText = this.add.text(panelX + panelWidth / 2, panelY + 35, '0.0 单位/秒', {
      fontSize: '16px',
      color: '#00aaff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 与黑洞距离
    this.add.text(panelX + panelWidth / 2, panelY + 55, '距黑洞中心', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.distanceText = this.add.text(panelX + panelWidth / 2, panelY + 75, '--- 单位', {
      fontSize: '16px',
      color: '#ff6600',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 逃逸速度参考（右侧面板）
    const rightPanelX = GAME_WIDTH - 180;
    const rightPanelY = 180;
    const rightPanelWidth = 160;
    const rightPanelHeight = 80;

    const rightPanel = this.add.graphics();
    rightPanel.fillStyle(0x1a1a3a, 0.8);
    rightPanel.fillRoundedRect(rightPanelX, rightPanelY, rightPanelWidth, rightPanelHeight, 10);
    rightPanel.lineStyle(2, 0x3a3a5a);
    rightPanel.strokeRoundedRect(rightPanelX, rightPanelY, rightPanelWidth, rightPanelHeight, 10);

    this.add.text(rightPanelX + rightPanelWidth / 2, rightPanelY + 15, '当前位置逃逸速度', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.escapeVelText = this.add.text(rightPanelX + rightPanelWidth / 2, rightPanelY + 45, '--- 单位/秒', {
      fontSize: '16px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);
  }

  /**
   * 创建速度选择按钮
   */
  private createVelocityButtons(): void {
    if (!this.levelSpecific) return;

    const velocities = this.levelSpecific.initialVelocityOptions;
    const startX = GAME_WIDTH / 2 - (velocities.length * 80) / 2;
    const y = 520;

    this.add.text(GAME_WIDTH / 2, y - 35, '选择初始速度（向右发射）:', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    velocities.forEach((velocity, i) => {
      const x = startX + i * 80 + 40;
      const btn = this.add.graphics();
      const isSelected = this.selectedInitialVelocity === velocity;

      btn.fillStyle(isSelected ? 0x4ade80 : 0x4a4a6a, isSelected ? 1 : 0.8);
      btn.fillRoundedRect(x - 35, y, 70, 45, 8);
      btn.lineStyle(2, isSelected ? 0x4ade80 : 0x6a6a8a);
      btn.strokeRoundedRect(x - 35, y, 70, 45, 8);

      this.add.text(x, y + 15, `${velocity.toFixed(0)}`, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }).setOrigin(0.5);

      this.add.text(x, y + 35, '单位/秒', {
        fontSize: '10px',
        color: '#888888',
        fontFamily: 'Arial',
      }).setOrigin(0.5);

      btn.setInteractive(
        new Phaser.Geom.Rectangle(x - 35, y, 70, 45),
        Phaser.Geom.Rectangle.Contains
      );

      btn.on('pointerdown', () => {
        if (this.gameState === 'idle') {
          this.selectedInitialVelocity = velocity;
          this.updateVelocityButtons();
          this.updatePhysicsDisplay();
        }
      });

      btn.on('pointerover', () => {
        if (this.gameState === 'idle') {
          btn.clear();
          btn.fillStyle(0x4ade80, 0.9);
          btn.fillRoundedRect(x - 35, y, 70, 45, 8);
          btn.strokeRoundedRect(x - 35, y, 70, 45, 8);
        }
      });

      btn.on('pointerout', () => {
        if (this.gameState === 'idle') {
          this.updateVelocityButtons();
        }
      });

      this.velocityButtons.push(btn);
    });
  }

  /**
   * 更新速度按钮状态
   */
  private updateVelocityButtons(): void {
    if (!this.levelSpecific) return;

    const velocities = this.levelSpecific.initialVelocityOptions;
    const startX = GAME_WIDTH / 2 - (velocities.length * 80) / 2;
    const y = 520;

    this.velocityButtons.forEach((btn, i) => {
      const velocity = velocities[i];
      if (velocity === undefined) return;
      const x = startX + i * 80 + 40;
      const isSelected = this.selectedInitialVelocity === velocity;

      btn.clear();
      btn.fillStyle(isSelected ? 0x4ade80 : 0x4a4a6a, isSelected ? 1 : 0.8);
      btn.fillRoundedRect(x - 35, y, 70, 45, 8);
      btn.lineStyle(2, isSelected ? 0x4ade80 : 0x6a6a8a);
      btn.strokeRoundedRect(x - 35, y, 70, 45, 8);
    });
  }

  /**
   * 创建开始按钮
   */
  private createStartButton(): void {
    this.startButton = this.add.graphics();
    this.startButton.fillStyle(0x4ade80, 1);
    this.startButton.fillRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
    this.startButton.lineStyle(2, 0x4ade80);
    this.startButton.strokeRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);

    this.add.text(GAME_WIDTH - 85, 545, '发射!', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.startButton.setInteractive(
      new Phaser.Geom.Rectangle(GAME_WIDTH - 150, 520, 130, 50),
      Phaser.Geom.Rectangle.Contains
    );
    this.startButton.on('pointerdown', () => this.startGame());

    this.startButton.on('pointerover', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x5aee90, 1);
      this.startButton?.fillRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
      this.startButton?.strokeRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
    });

    this.startButton.on('pointerout', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x4ade80, 1);
      this.startButton?.fillRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
      this.startButton?.strokeRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
    });
  }

  /**
   * 设置输入事件
   */
  private setupInputEvents(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState !== 'idle') return;

      if (event.key === 'Enter') {
        this.startGame();
      }

      if (event.key === 'r' || event.key === 'R') {
        this.resetGame();
      }
    });
  }

  /**
   * 更新物理显示
   */
  private updatePhysicsDisplay(): void {
    if (!this.blackhole) return;

    const position = Vec2(this.stickmanPosition.x, this.stickmanPosition.y);
    const distance = this.blackhole.calculateDistance(position);

    // 计算当前位置的逃逸速度
    const potentialResult = this.blackhole.calculateGravitationalPotential(position);
    const escapeVelocity = potentialResult.escapeVelocity;

    // 当前速度大小
    const velocityMagnitude = Math.sqrt(
      this.stickmanVelocity.x * this.stickmanVelocity.x +
      this.stickmanVelocity.y * this.stickmanVelocity.y
    );

    this.velocityText?.setText(`${velocityMagnitude.toFixed(1)} 单位/秒`);
    this.distanceText?.setText(`${distance.toFixed(1)} 单位`);
    this.escapeVelText?.setText(`${escapeVelocity.toFixed(1)} 单位/秒`);

    // 如果在事件视界内，显示特殊警告
    if (this.blackhole.isInsideEventHorizon(position)) {
      this.distanceText?.setColor('#ff0000');
      this.hintText?.setText('⚠️ 已进入事件视界！无法逃逸！');
    } else if (this.blackhole.isNearEventHorizon(position, 2)) {
      this.distanceText?.setColor('#ff6600');
      this.hintText?.setText('⚠️ 接近事件视界！需要更高速度才能逃逸！');
    } else {
      this.distanceText?.setColor('#ff6600');
      this.hintText?.setText(this.levelSpecific?.hint ?? '选择初速度，逃离黑洞引力场');
    }
  }

  /**
   * 更新火柴人运动（受引力影响）
   */
  private updateStickmanMovement(delta: number): void {
    if (!this.blackhole) return;

    const dt = delta / 1000;

    // 获取当前位置
    const position = Vec2(this.stickmanPosition.x, this.stickmanPosition.y);

    // 检测是否在事件视界内
    if (this.blackhole.isInsideEventHorizon(position)) {
      this.gameState = 'failed';
      this.showFailResult('被黑洞捕获！进入事件视界，无法逃逸！');
      return;
    }

    // 计算引力加速度
    const gravitationalAcc = this.blackhole.calculateGravitationalAcceleration(position);

    // 更新速度（引力加速度指向黑洞）
    this.stickmanVelocity.x += gravitationalAcc.x * dt * 50; // 缩放因子
    this.stickmanVelocity.y += gravitationalAcc.y * dt * 50;

    // 更新位置
    this.stickmanPosition.x += this.stickmanVelocity.x * dt;
    this.stickmanPosition.y += this.stickmanVelocity.y * dt;

    // 记录轨迹
    this.trajectoryPoints.push({ x: this.stickmanPosition.x, y: this.stickmanPosition.y });
    if (this.trajectoryPoints.length > 200) {
      this.trajectoryPoints.shift();
    }

    // 绘制轨迹
    this.drawTrajectory();

    // 渲染火柴人
    this.renderStickman();

    // 更新显示
    this.updatePhysicsDisplay();
  }

  /**
   * 绘制轨迹
   */
  private drawTrajectory(): void {
    if (!this.trajectoryGraphics || this.trajectoryPoints.length < 2) return;

    this.trajectoryGraphics.clear();

    // 根据距离黑洞远近变化颜色
    const dist = this.blackhole?.calculateDistance(Vec2(this.stickmanPosition.x, this.stickmanPosition.y)) ?? 100;
    const rs = this.blackhole?.getSchwarzschildRadius() ?? 100;

    let trailColor: number;
    if (dist < rs * 1.5) {
      trailColor = 0xff0000; // 红色 - 非近
    } else if (dist < rs * 3) {
      trailColor = 0xff6600; // 橙色 - 较近
    } else {
      trailColor = 0x00aaff; // 蓝色 - 安全
    }

    this.trajectoryGraphics.lineStyle(2, trailColor, 0.6);
    this.trajectoryGraphics.beginPath();

    const firstPoint = this.trajectoryPoints[0];
    if (firstPoint) {
      this.trajectoryGraphics.moveTo(firstPoint.x, GAME_HEIGHT - firstPoint.y);
    }

    for (let i = 1; i < this.trajectoryPoints.length; i++) {
      const point = this.trajectoryPoints[i];
      if (point) {
        this.trajectoryGraphics.lineTo(point.x, GAME_HEIGHT - point.y);
      }
    }

    this.trajectoryGraphics.strokePath();
  }

  /**
   * 更新视觉效果
   */
  private updateVisualEffects(): void {
    // 动态更新黑洞发光效果
    if (this.blackholeGraphics && this.blackhole) {
      const info = this.blackhole.getInfo();
      const screenX = info.position.x;
      const screenY = GAME_HEIGHT - info.position.y;

      // 吸积盘脉动效果
      const pulse = Math.sin(this.elapsedTime * 3) * 0.2 + 0.8;

      this.blackholeGraphics.clear();

      // 事件视界
      this.blackholeGraphics.fillStyle(0x000000, 1);
      this.blackholeGraphics.fillCircle(screenX, screenY, info.schwarzschildRadius);
      this.blackholeGraphics.lineStyle(3, 0xff0000, 0.8);
      this.blackholeGraphics.strokeCircle(screenX, screenY, info.schwarzschildRadius);

      // 吸积盘
      this.blackholeGraphics.lineStyle(8, 0xff6600, pulse * 0.6);
      this.blackholeGraphics.strokeCircle(screenX, screenY, info.schwarzschildRadius * 2);
      this.blackholeGraphics.fillStyle(0xff3300, pulse * 0.2);
      this.blackholeGraphics.fillCircle(screenX, screenY, info.schwarzschildRadius * 3);
    }
  }

  /**
   * 开始游戏
   */
  private startGame(): void {
    if (this.gameState !== 'idle') return;
    if (this.selectedInitialVelocity === 0) {
      this.hintText?.setText('请先选择初始速度！');
      return;
    }

    this.gameState = 'running';
    this.elapsedTime = 0;

    // 设置初始速度（向右，对抗引力）
    this.stickmanVelocity = {
      x: this.selectedInitialVelocity,
      y: 0,
    };

    // 清除轨迹
    this.trajectoryPoints = [];
    if (this.trajectoryGraphics) {
      this.trajectoryGraphics.clear();
    }

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 更新开始按钮状态
    this.updateStartButtonState('running');

    // 更新提示
    this.hintText?.setText('正在逃离黑洞引力场...观察轨迹弯曲！');
  }

  /**
   * 更新开始按钮状态
   */
  private updateStartButtonState(state: GameState): void {
    if (!this.startButton) return;

    this.startButton.clear();

    if (state === 'running') {
      this.startButton.fillStyle(0x6a6a8a, 1);
    } else {
      this.startButton.fillStyle(0x4ade80, 1);
    }
    this.startButton.fillRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
    this.startButton.strokeRoundedRect(GAME_WIDTH - 150, 520, 130, 50, 10);
  }

  /**
   * 检测游戏结束
   */
  private checkGameEnd(): void {
    if (!this.levelSpecific || !this.blackhole) return;

    // 检测是否到达安全区域
    const dx = this.stickmanPosition.x - this.levelSpecific.safeZoneX;
    const dy = this.stickmanPosition.y - this.levelSpecific.safeZoneY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= this.levelSpecific.safeZoneRadius) {
      this.gameState = 'success';
      this.showSuccessResult();
      this.updateStartButtonState('idle');
      return;
    }

    // 检测是否超出边界
    if (
      this.stickmanPosition.x < 0 ||
      this.stickmanPosition.x > GAME_WIDTH ||
      this.stickmanPosition.y < 0 ||
      this.stickmanPosition.y > GAME_HEIGHT
    ) {
      this.gameState = 'failed';
      this.showFailResult('超出游戏边界！');
      this.updateStartButtonState('idle');
      return;
    }

    // 检测是否被黑洞捕获
    if (this.blackhole.isInsideEventHorizon(Vec2(this.stickmanPosition.x, this.stickmanPosition.y))) {
      this.gameState = 'failed';
      this.showFailResult('被黑洞捕获！进入事件视界！');
      this.updateStartButtonState('idle');
      return;
    }
  }

  /**
   * 显示成功结果
   */
  private showSuccessResult(): void {
    const gameState = getGameState();
    gameState.completeLevel(16, this.elapsedTime);

    const velocityMagnitude = Math.sqrt(
      this.stickmanVelocity.x * this.stickmanVelocity.x +
      this.stickmanVelocity.y * this.stickmanVelocity.y
    );

    this.resultText?.setText(`🎉 成功！逃离黑洞引力场！用时 ${this.elapsedTime.toFixed(1)} 秒`)
      .setColor('#4ade80')
      .setVisible(true);

    this.hintText?.setText(
      `最终速度: ${velocityMagnitude.toFixed(1)} 单位/秒。引力弯曲了轨迹，但成功逃逸！`
    );

    this.createCompletionButtons();
  }

  /**
   * 创建完成后的按钮
   */
  private createCompletionButtons(): void {
    const y = GAME_HEIGHT / 2 + 60;

    const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, y, '下一关', {
      fontSize: '18px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    nextBtn.on('pointerdown', () => {
      const gameState = getGameState();
      if (gameState.isLevelUnlocked(17)) {
        this.scene.start('Level17Scene');
      } else {
        this.scene.start('LevelSelectScene');
      }
    });

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, y, '返回', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  /**
   * 显示失败结果
   */
  private showFailResult(reason: string): void {
    this.resultText?.setText(`❌ ${reason}`)
      .setColor('#ff6b6b')
      .setVisible(true);

    this.hintText?.setText('提示：选择足够高的初速度，以对抗黑洞引力逃逸到安全区域');
  }

  /**
   * 重置游戏
   */
  private resetGame(): void {
    this.gameState = 'idle';
    this.elapsedTime = 0;
    this.stickmanVelocity = { x: 0, y: 0 };
    this.selectedInitialVelocity = 0;

    // 重置位置
    if (this.levelConfig?.player) {
      this.stickmanPosition = {
        x: this.levelConfig.player.startPosition.x,
        y: this.levelConfig.player.startPosition.y,
      };
    } else {
      this.stickmanPosition = { x: 500, y: 300 };
    }

    // 清除轨迹
    this.trajectoryPoints = [];
    if (this.trajectoryGraphics) {
      this.trajectoryGraphics.clear();
    }

    // 渲染火柴人
    this.renderStickman();

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 重置提示
    this.hintText?.setText(this.levelSpecific?.hint ?? '选择初速度，逃离黑洞引力场');

    // 重置按钮
    this.updateStartButtonState('idle');
    this.updateVelocityButtons();
    this.updatePhysicsDisplay();
  }

  /**
   * 显示教程
   */
  private showTutorial(): void {
    const tutorialData = getTutorial('level-16');
    if (!tutorialData) return;

    this.tutorialUI = new TutorialUI(this, {
      levelNumber: 16,
      tutorialData,
      onStart: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
      onSkip: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
    });
  }

  /**
   * 获取当前游戏状态
   */
  getGameState(): GameState {
    return this.gameState;
  }

  /**
   * 获取当前时间
   */
  getElapsedTime(): number {
    return this.elapsedTime;
  }

  /**
   * 获取火柴人位置
   */
  getStickmanPosition(): { x: number; y: number } {
    return this.stickmanPosition;
  }

  /**
   * 获取火柴人速度
   */
  getStickmanVelocity(): { x: number; y: number } {
    return this.stickmanVelocity;
  }

  /**
   * 获取黑洞模块
   */
  getBlackhole(): Blackhole | null {
    return this.blackhole;
  }
}